import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";

const MdlAddExamParticipatingClass = ({ show, onHide, examId, sessionId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [mClassData, setClass] = useState([]);
  const [classBoard, setClassBoard] = useState([]);
  const [classMedium, setClassMedium] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [mFilteredClassSectionList, setFilteredClassSectionList] = useState([]);
  const [alreadyAddedClasses, setAlreadyAddedClasses] = useState([]); // Track classes already in exam
  
  // Selection state: track selected classes and subjects per class
  const [selectedClasses, setSelectedClasses] = useState({}); // key -> boolean
  const [selectedSubjectsByClass, setSelectedSubjectsByClass] = useState({}); // key -> Set(subject)

  useEffect(() => {
    if (show && sessionId && examId) {
      fetchClassData();
      fetchAlreadyAddedClasses();
    } else if (!show) {
      // Reset when modal closes
      setSelectedBoard("");
      setSelectedMedium("");
      setSelectedClasses({});
      setSelectedSubjectsByClass({});
      setFilteredClassSectionList([]);
      setAlreadyAddedClasses([]);
    }
  }, [show, sessionId, examId]);

  useEffect(() => {
    getClassList();
  }, [mClassData, alreadyAddedClasses]);

  useEffect(() => {
    getClassList();
  }, [selectedBoard, selectedMedium, alreadyAddedClasses]);

  const fetchAlreadyAddedClasses = async () => {
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const FUNCTION_NAME = "getClassWithSubjectsPartAndPaper.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
      const params = {
        school_code: SCHOOL_CODE,
        exam_id: examId,
        session_id: sessionId,
      };
      
      const res = await axios.get(URL, { params });
      if (res.data?.api_status === "OK") {
        // Extract class_id and section_id pairs from already added classes
        const addedClasses = (res.data.data || []).map(item => ({
          class_id: item.class_id,
          section_id: item.section_id || "No"
        }));
        setAlreadyAddedClasses(addedClasses);
      }
    } catch (error) {
      console.error("Error fetching already added classes:", error);
      // Don't show error toast, just continue without filtering
    }
  };

  const fetchClassData = () => {
    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getClassWithSubjects.php";
    const URL =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      FUNCTION_NAME;
    const params = {
      school_code: SCHOOL_CODE,
      session_id: sessionId,
    };
    const queryString = new URLSearchParams(params).toString();
    console.log("getClassWithSubjects.php Full URL:", `${URL}${queryString}`);

    axios
      .get(URL, { params: params })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setClass(response.data?.class || []);
          setClassBoard(response.data?.board || []);
          setClassMedium(response.data?.medium || []);

          if (response.data?.board && response.data.board.length > 0) {
            setSelectedBoard(response.data.board[0].board_id);
          }
          if (response.data?.medium && response.data.medium.length > 0) {
            setSelectedMedium(response.data.medium[0].medium_id);
          }
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("API Error:", error);
        myToast.notify_error("Error loading class data");
      });
  };

  const onBoardChange = (event) => {
    setSelectedBoard(event.target.value);
  };

  const onMediumChange = (event) => {
    setSelectedMedium(event.target.value);
  };

  const getClassList = () => {
    const filteredClassSectionList = [];
    const filteredClassList = mClassData.filter(
      (item) =>
        item.board_id === selectedBoard && item.medium_id === selectedMedium
    );

    // Helper function to check if class-section is already added
    const isAlreadyAdded = (classId, sectionId) => {
      return alreadyAddedClasses.some(
        added => added.class_id === classId && added.section_id === (sectionId || "No")
      );
    };

    filteredClassList.forEach((item) => {
      if (item.section === "No") {
        item["section_id"] = "No";
        // Skip if already added
        if (!isAlreadyAdded(item.class_id, "No")) {
          if (item.subject) item["subject"] = item.subject;
          filteredClassSectionList.push(item);
        }
      } else {
        const tempSection = item.section;
        tempSection.forEach((item2) => {
          // Skip if already added
          if (!isAlreadyAdded(item2.class_id, item2.section_id)) {
            item2["board_id"] = selectedBoard;
            item2["board_name"] = item.board_name;
            item2["medium_id"] = selectedMedium;
            item2["medium_name"] = item.medium_name;
            if (item.subject) item2["subject"] = item.subject;
            filteredClassSectionList.push(item2);
          }
        });
      }
    });

    // Sort by class_id
    filteredClassSectionList.sort((a, b) => {
      const aId = parseInt(a.class_id, 10);
      const bId = parseInt(b.class_id, 10);
      if (!isNaN(aId) && !isNaN(bId)) return aId - bId;
      return String(a.class_id).localeCompare(String(b.class_id));
    });

    setFilteredClassSectionList(filteredClassSectionList);
    // Initialize selection maps
    const nextSelectedClasses = { ...selectedClasses };
    const nextSelectedSubjects = { ...selectedSubjectsByClass };
    filteredClassSectionList.forEach((item) => {
      const key = `${item.class_id}-${item.section_id}`;
      if (nextSelectedClasses[key] === undefined) nextSelectedClasses[key] = false;
      if (!nextSelectedSubjects[key]) nextSelectedSubjects[key] = new Set();
    });
    setSelectedClasses(nextSelectedClasses);
    setSelectedSubjectsByClass(nextSelectedSubjects);
  };

  // Helpers for selection
  const getKey = (item) => `${item.class_id}-${item.section_id}`;
  
  const getSubjectsForItem = (item) => {
    const itemSubjects = Array.isArray(item?.subject) ? item.subject : [];
    return itemSubjects.map((sj) => sj.subject_name || sj.exam_paper_name).filter(Boolean);
  };

  const toggleClassSelected = (item) => {
    const key = getKey(item);
    const newVal = !selectedClasses[key];
    const nextClasses = { ...selectedClasses, [key]: newVal };
    const nextSubjects = { ...selectedSubjectsByClass };
    if (newVal) {
      if (!nextSubjects[key]) nextSubjects[key] = new Set();
    } else {
      nextSubjects[key] = new Set();
    }
    setSelectedClasses(nextClasses);
    setSelectedSubjectsByClass(nextSubjects);
  };

  const isSubjectSelected = (item, subject) => {
    const key = getKey(item);
    const setForClass = selectedSubjectsByClass[key];
    return setForClass ? setForClass.has(subject) : false;
  };

  const toggleSubjectSelected = (item, subject) => {
    const key = getKey(item);
    const nextSubjects = { ...selectedSubjectsByClass };
    const setForClass = new Set(nextSubjects[key] || []);
    if (setForClass.has(subject)) {
      setForClass.delete(subject);
    } else {
      setForClass.add(subject);
    }
    nextSubjects[key] = setForClass;
    const nextClasses = { ...selectedClasses };
    nextClasses[key] = setForClass.size > 0 ? true : nextClasses[key];
    setSelectedSubjectsByClass(nextSubjects);
    setSelectedClasses(nextClasses);
  };

  const toggleAllSubjects = (item) => {
    const key = getKey(item);
    const allSubjects = getSubjectsForItem(item);
    const currentSet = selectedSubjectsByClass[key] || new Set();
    const allSelected = currentSet.size === allSubjects.length && allSubjects.length > 0;
    const nextSubjects = { ...selectedSubjectsByClass };
    if (allSelected) {
      nextSubjects[key] = new Set();
      setSelectedSubjectsByClass(nextSubjects);
    } else {
      nextSubjects[key] = new Set(allSubjects);
      setSelectedSubjectsByClass(nextSubjects);
      setSelectedClasses({ ...selectedClasses, [key]: true });
    }
  };

  const handleSave = async () => {
    // Build selections
    const classSelections = [];
    mFilteredClassSectionList.forEach((item) => {
      const key = getKey(item);
      const subjectsSet = selectedSubjectsByClass[key];
      if (subjectsSet && subjectsSet.size > 0) {
        const itemSubjects = Array.isArray(item.subject) ? item.subject : [];
        const selectedSubjectObjs = Array.from(subjectsSet)
          .map((name) => {
            const match = itemSubjects.find((sj) => sj.subject_name === name || sj.exam_paper_name === name);
            if (!match || !match.subject_id) return null;
            return { subject_id: String(match.subject_id), isChecked: "true" };
          })
          .filter(Boolean);

        classSelections.push({
          class_id: item.class_id,
          section_id: item.section_id,
          isChecked: "true",
          subject: JSON.stringify(selectedSubjectObjs),
        });
      }
    });

    if (classSelections.length === 0) {
      myToast.notify_warning("Select at least one subject for a class");
      return;
    }

    try {
      setLoading(true);
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const STAFF_DETAILS = SessionManager.getStaffDetails();
      const STAFF_ID = STAFF_DETAILS?.staff_id || "";

      if (!STAFF_ID) {
        myToast.notify_error("Staff ID is missing");
        return;
      }
      if (!examId) {
        myToast.notify_error("Exam ID is missing");
        return;
      }
      if (!SCHOOL_CODE) {
        myToast.notify_error("School code is missing");
        return;
      }

      const FUNCTION_NAME = "AddExamParticipatingClass.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

      // Add each class with subjects
      let successCount = 0;
      let failedClasses = [];

      for (const classSelection of classSelections) {
        const params = {
          staff_id: String(STAFF_ID),
          exam_id: String(examId),
          class_id: String(classSelection.class_id),
          section_id: String(classSelection.section_id || "No"),
          session_id: String(sessionId || ""),
          school_code: String(SCHOOL_CODE),
          subject: classSelection.subject
        };

        // Log full URL for debugging
        try {
          const queryString = new URLSearchParams(params).toString();
          console.log("AddExamParticipatingClass.php Full URL:", `${URL}?${queryString}`);
        } catch (e) {
          // Ignore logging errors
        }
        
        try {
          const response = await axios.get(URL, { params });
          if (response.data?.api_status === "OK") {
            successCount++;
          } else {
            failedClasses.push({
              class: classSelection.class_id,
              error: response.data?.api_remark || "Failed to add class"
            });
          }
        } catch (error) {
          failedClasses.push({
            class: classSelection.class_id,
            error: error.response?.data?.api_remark || error.message || "Error adding class"
          });
        }
      }

      if (failedClasses.length === 0) {
        myToast.notify_success(`${successCount} class${successCount > 1 ? 'es' : ''} added to exam successfully`);
        onSaved && onSaved();
        onHide();
      } else if (successCount > 0) {
        myToast.notify_warning(`${successCount} class${successCount > 1 ? 'es' : ''} added, ${failedClasses.length} failed`);
        onSaved && onSaved();
        onHide();
      } else {
        myToast.notify_error(`Failed to add classes: ${failedClasses[0].error}`);
      }
    } catch (e) {
      console.error("Error adding classes:", e);
      myToast.notify_error(e.message || "Error adding classes");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} animation={false} size="lg" centered style={{ zIndex: 1050 }}>
      <Modal.Header closeButton className="mdladdpaper-modal-header align-items-center">
        <div className="mdladdpaper-title">Add Participating Classes and Subjects</div>
      </Modal.Header>
      <Modal.Body className="MdlEditPaper-modal-body" style={{ margin: "10px", maxHeight: "70vh", overflowY: "auto" }}>
        {loading && !mClassData.length ? (
          <div className="text-center" style={{ padding: "40px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p style={{ marginTop: "10px", color: "#6b7280" }}>Loading classes...</p>
          </div>
        ) : (
          <>
            <div className="row" style={{ margin: 0, marginBottom: "16px" }}>
              <div className="col-md-6" style={{ padding: "0 8px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", color: COLORS.primary_color_shade5, fontSize: "14px", marginBottom: "6px", fontWeight: 600 }}>
                  <i className="fa fa-university"></i>
                  Select Board
                </label>
                <select
                  className="select-field"
                  onChange={onBoardChange}
                  value={selectedBoard}
                  style={{
                    width: "100%",
                    height: "36px",
                    borderRadius: "6px",
                    border: `1px solid ${COLORS.primary_color_shade5}`,
                    padding: "0 10px",
                    fontSize: "13px"
                  }}
                >
                  {classBoard.map((item) => (
                    <option key={item.board_id} value={item.board_id}>
                      {item.board_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6" style={{ padding: "0 8px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", color: COLORS.primary_color_shade5, fontSize: "14px", marginBottom: "6px", fontWeight: 600 }}>
                  <i className="fa fa-language"></i>
                  Select Medium
                </label>
                <select
                  className="select-field"
                  onChange={onMediumChange}
                  value={selectedMedium}
                  style={{
                    width: "100%",
                    height: "36px",
                    borderRadius: "6px",
                    border: `1px solid ${COLORS.primary_color_shade5}`,
                    padding: "0 10px",
                    fontSize: "13px"
                  }}
                >
                  {classMedium.map((item) => (
                    <option key={item.medium_id} value={item.medium_id}>
                      {item.medium_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <h4 style={{ margin: 0, color: COLORS.primary_color_shade5, fontSize: "16px" }}>
                Select Classes and Subjects
              </h4>
              <span className="badge bg-primary" style={{ fontSize: "11px" }}>
                {mFilteredClassSectionList.length} classes
              </span>
            </div>

            {mFilteredClassSectionList && mFilteredClassSectionList.length === 0 && (
              <div className="text-center" style={{ padding: "24px 0" }}>
                <i className="fa fa-folder-open fa-2x text-muted"></i>
                <div className="text-muted" style={{ marginTop: "6px", fontSize: "13px" }}>
                  No classes found for selected board/medium.
                </div>
              </div>
            )}

            {mFilteredClassSectionList &&
              mFilteredClassSectionList.map((item, index) => {
                const key = getKey(item);
                const selectedCount = (selectedSubjectsByClass[key] && selectedSubjectsByClass[key].size) || 0;
                return (
                  <section
                    key={`${key}-${index}`}
                    style={{
                      border: selectedClasses[key] ? "1px solid transparent" : "1px solid #cfe8ff",
                      background: selectedClasses[key]
                        ? "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, #2ecc71, #a6f3c5) border-box"
                        : "#ffffff",
                      borderRadius: "8px",
                      marginBottom: "12px"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 10px",
                        borderBottom: selectedClasses[key] ? "1px solid transparent" : "1px solid #cfe8ff",
                        background: selectedClasses[key]
                          ? "linear-gradient(#c8ffd5, #c8ffd5) padding-box, linear-gradient(90deg, #2ecc71, #a6f3c5) border-box"
                          : "#c3d8ff",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedClasses[key]}
                            onChange={() => toggleClassSelected(item)}
                            style={{ cursor: "pointer" }}
                          />
                          <h4 style={{ margin: 0, color: selectedClasses[key] ? COLORS.primary_color_shade5 : "#111", fontSize: "14px", fontWeight: 600 }}>
                            {item.section_id === "No" ? item.class_name : item.class_name + ":" + item.section_name}
                          </h4>
                        </label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span className="badge" style={{ background: "#5a8dfd", color: "#ffffff", fontSize: "11px", padding: "4px 10px", height: "26px", display: "inline-flex", alignItems: "center", borderRadius: "4px" }}>
                          {selectedCount} selected
                        </span>
                        <button
                          className="btn btn-sm"
                          onClick={() => toggleAllSubjects(item)}
                          style={{ 
                            background: "#5a8dfd", 
                            borderColor: "#3f79fa", 
                            color: "#ffffff", 
                            fontSize: "11px",
                            padding: "4px 10px",
                            height: "26px",
                            minWidth: "90px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: 0
                          }}
                        >
                          {(() => {
                            const all = getSubjectsForItem(item);
                            const cur = selectedSubjectsByClass[key] || new Set();
                            const allSelected = cur.size === all.length && all.length > 0;
                            return allSelected ? "Deselect All" : "Select All";
                          })()}
                        </button>
                      </div>
                    </div>

                    <div style={{ padding: "8px 10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {getSubjectsForItem(item).map((subject, sIdx) => {
                        const isChecked = isSubjectSelected(item, subject);
                        return (
                          <label
                            key={sIdx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "6px 10px",
                              border: `1px solid ${COLORS.primary_color_shade5}`,
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: "#fff",
                              color: isChecked ? COLORS.primary_color_shade5 : "#333",
                              fontSize: "12px",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSubjectSelected(item, subject)}
                              style={{ cursor: "pointer" }}
                            />
                            <span>{subject}</span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="mdleditexam-modal-footer">
        <Button variant="primary" className="mdleditexam-btn" onClick={handleSave} disabled={loading || mFilteredClassSectionList.length === 0}>
          {loading ? "Saving..." : "Add Classes"}
        </Button>
        <Button variant="secondary" className="mdleditexam-btn" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlAddExamParticipatingClass;
